import { isError } from "@sindresorhus/is";
import { useTranslation } from "gatsby-plugin-react-i18next";
import { useEffect, useState, type ChangeEventHandler, type FC } from "react";
import { stripHtml } from "string-strip-html";
import {
	getVariantPrice,
	moneyToNumber,
} from "../../../../utils/commercetools/money";
import { BazaarWidget } from "../../components/BazaarVoiceWidget/BazaarVoiceWidget";
import GiftCard, {
	type GiftCardFormValues,
} from "../../components/GiftCard/GiftCard";
import MoreSalesList from "../../components/MoreSalesList";
import MoreSalesBanner from "../../components/MoreSalesList/components/MoreSalesBanner";
import PriceModule from "../../components/PriceModule";
import type { ProductCardProps } from "../../components/ProductCard/ProductCard";
import ProductGallery from "../../components/ProductGallery";
import ProductPageComponent from "../../components/ProductPage";
import ProductPane from "../../components/ProductPage/components/ProductPane/ProductPane";
import ProductStickyNav from "../../components/ProductStickyNav";
import BackgroundTheme from "../../components/general/BackgroundTheme";
import GridItem from "../../components/general/GridItem";
import Icon from "../../components/general/Icon";
import type { IconType } from "../../components/general/Icon/Icon";
import ImageContainer from "../../components/general/ImageContainers";
import Link from "../../components/general/Link";
import ShareListItem from "../../components/general/Modal/components/ShareListItem";
import type { PaymentItemType } from "../../components/general/Payment/Payment";
import ProductOptions from "../../components/general/ProductOptions";
import ScrollSpy from "../../components/general/ScrollSpy";
import ScrollSpyItem from "../../components/general/ScrollSpy/ScrollSpyItem";
import SingleInputForm from "../../components/general/SingleInputForm";
import * as tabStyles from "../../components/general/Tabs/Tabs.module.scss";
import TabGroup from "../../components/general/Tabs/components/TabGroup";
import type { TabGroupTabsProps } from "../../components/general/Tabs/components/TabGroup/TabGroup";
import VariantPicker from "../../components/general/VariantPicker";
import VariantItem from "../../components/general/VariantPicker/components/VariantItem";
import ModuleRenderer, {
	type ModuleRendererProps,
} from "../../components/module-renderer";
import { Breadcrumbs } from "../../containers/breadcrumbs";
import PageContainerComponent from "../../containers/page";
import { useAppState } from "../../context/app/context";
import { useHasBazaarVoice } from "../../hooks/use-bazaarvoice";
import { useCartService } from "../../hooks/use-cart-service";
import { useGiftCardErrorMessages } from "../../hooks/use-gift-card-error-messages";
import {
	useIsDesktop,
	useIsMinMaxWidth,
	useIsMinWidth,
} from "../../hooks/use-is-screensize";
import { useKlaviyoViewedProduct } from "../../hooks/use-klaviyo";
import { useProductGiftCards } from "../../hooks/use-product-gift-cards";
import useScrollBlock from "../../hooks/useScrollBlock";
import AccordionModule from "../../modules/accordion-module";
import { getVariants } from "../../templates/product-page/product-page.helper";
import ProductPageModal, {
	type ProductPageModalProps,
} from "../../templates/product-page/product-page.modal";
import type { Mutable } from "../../type-definitions/utility-types";
import { chunks } from "../../utils/array";
import { BabyListButton } from "../../utils/babylist";
import type { Breadcrumb } from "../../utils/breadcrumbs";
import { sendBrowserEvent } from "../../utils/browser-events";
import { formatMoney, formatPrice } from "../../utils/cart/currency";
import { cartHasGiftCardPayment } from "../../utils/gift-card";
import type { Material } from "../../utils/materials";
import { mapStoryblokVariantToProductCard } from "../../utils/product-card";
import { facebook, pinterest } from "../../utils/share";
import { isInPreviewEditor } from "../../utils/storyblok/is-in-preview-editor";
import {
	getVariantColorLabelFromFragment,
	getVariantFabricLabelFromFragment,
	getVariantLabelFromVariant,
	getVariantProductImages,
	getVariantStringAttribute,
} from "../../utils/storyblok/product-variant";
import { notNull, notUndefined } from "../../utils/type-guards/general";
import { Materials } from "./materials/product-page-materials.component";
import MyRegistryButton from "./product-page.myregistry-button";

export type buttonProps = {
	href: string;
	label: string;
	icon?: IconType;
	target?: string;
}[];

export type ProductPageProps = {
	product: Queries.CommerceToolsProductFragment;
	variant: Queries.ProductVariantFragment;
	modules?: ModuleRendererProps["modules"];
	tabs: Queries.ProductTabFragment[];
	accessories?: Queries.StoryblokVariantProductFragment[];
	priceModuleButtons?: buttonProps;
	productDetails: Queries.ProductDetailsFragment[];
	paymentMethods: PaymentItemType[];
	breadcrumbs: Breadcrumb[];
	materials?: Material[];
	inStock: boolean;
	badge?: string;
	backgroundColor?: string;
	stockQuantity: number;
	isGiftCard?: boolean;
	shippingInfo?: string;
	deliveryInfo?: string;
	giftWrapInfo?: string;
	showBabyListButton: boolean;
	showMyRegistryButton?: boolean;
};

let scrollPos = 0;

const ProductPage: FC<ProductPageProps> = ({
	product,
	variant,
	modules,
	tabs,
	accessories,
	priceModuleButtons,
	productDetails,
	paymentMethods,
	breadcrumbs,
	materials,
	inStock,
	badge,
	backgroundColor,
	stockQuantity,
	isGiftCard = false,
	shippingInfo,
	deliveryInfo,
	giftWrapInfo,
	showBabyListButton,
	showMyRegistryButton,
}) => {
	const { t } = useTranslation();
	const { addItemsToCart, cart } = useCartService();
	const { siteConfig, userCountry } = useAppState();
	const [selectedTabIndex, setSelectedTabIndex] = useState(0);
	const variants = getVariants(product);
	const { giftCards } = useProductGiftCards();
	const isDesktop = useIsDesktop();

	const color = getVariantStringAttribute(variant, "color");
	const fabric = getVariantStringAttribute(variant, "fabric");
	const toy = getVariantStringAttribute(variant, "toy");

	const disableNotifyMe = Boolean(
		variant.storyblok_variant_pages?.[0]?.disable_notify_me,
	);

	const variantName = [color, fabric].filter(Boolean).join(" - ");

	const [activeVariant] = useState(
		getVariants(product).find((p) => p.id === variant.id),
	);

	const [waitListErrorMessage, setWaitListErrorMessage] = useState<
		undefined | string
	>();
	const [waitListSuccessMessage, setWaitListSuccessMessage] = useState<
		undefined | string
	>();
	const [waitListLoading, setWaitListLoading] = useState(false);
	const [waitListInput, setWaitListInput] = useState("");
	const [waitListShowInput, setWaitListShowInput] = useState(false);

	const waitListToggleInput = () => {
		setWaitListShowInput(!waitListShowInput);
	};

	const onWaitListInputChange: ChangeEventHandler<HTMLInputElement> = (e) => {
		setWaitListInput(e.currentTarget.value);
	};

	const performRequest = async () => {
		return await fetch("/api/custom-objects/add-notify-me", {
			method: "POST",
			headers: {
				"content-type": "application/json",
			},
			body: JSON.stringify({
				email: waitListInput,
				productId: product.id,
				variantId: activeVariant?.id,
				storeKey: siteConfig.store,
				siteUrl: window.location.origin + window.location.pathname,
				language: siteConfig.language,
			}),
		});
	};

	const onWaitListFormSubmit = async () => {
		setWaitListLoading(true);
		try {
			const response = await performRequest();
			if (response.status === 200) {
				setWaitListSuccessMessage(
					t(
						"You have successfully signed up! You will be notified when the product is back in stock.",
					),
				);
				setWaitListInput("");
			} else {
				// TODO Add type guard
				const data = (await response.json()) as {
					error: { message: string | undefined };
				};
				setWaitListErrorMessage(data.error.message ?? "");
			}
		} catch (error) {
			console.error(error);
		}
		setWaitListLoading(false);
	};

	const onChangeTab = (id: number) => {
		setSelectedTabIndex(id);
	};
	const stickyNavAnchorTagTabs: TabGroupTabsProps = [
		{
			id: "product",
			label: t("Product"),
			cta: { label: "", link: "#product" },
		},
		{
			id: "product-details",
			label: t("Product details"),
			cta: { label: "", link: "#product-details" },
		},
		{
			id: "reviews",
			label: t("Reviews"),
			cta: { label: "", link: "#reviews" },
		},
	];

	const [activeStickyNavLink, setActiveStickyNavLink] = useState<string>("");
	const [hideStickyNav, setHideStickyNav] = useState<boolean>(true);
	const productBuyButtonId = "product-buy-button";
	const onChangeStickyNavLink = (index?: string) => {
		if (index) {
			if (index === productBuyButtonId) {
				setHideStickyNav(true);
			} else {
				setHideStickyNav(false);
			}
			setActiveStickyNavLink(index);
		}
	};
	const buttonText = t("Add to cart");
	const ariaLabelLink = `${t("More about")} ${product.name || t("product")}`;

	const isMobile = useIsMinWidth(450);
	const isMinMax = useIsMinMaxWidth(768, 1130);

	const accessoryProductCardProps = isInPreviewEditor()
		? accessories?.map(() => placeholderProduct)
		: accessories
				?.map((variant) => {
					return mapStoryblokVariantToProductCard(
						variant,
						isMinMax || !isMobile ? "icon" : "standard",
						addItemsToCart,
						buttonText,
						siteConfig.currency,
						userCountry,
						undefined,
						ariaLabelLink,
						t("Out of stock"),
					);
				})
				.filter(notUndefined)
				.filter(notNull);

	const images = getVariantProductImages(variant);

	const [productImage, firstLifestyleImage, ...restImages] = images ?? [];

	const accordionItems = productDetails.map((detail) => ({
		label: detail.label ?? "",
		content: detail.content ?? "",
		icon: detail.icon ?? "",
		openByDefault: detail.open_by_default,
	}));

	const sideBySideImages = chunks(restImages, 2);

	const addToCart = async () => {
		try {
			await addItemsToCart([{ product, variant }]);
		} catch (error) {
			throw isError(error)
				? error
				: new Error("An error occured while adding an item to the cart");
		}
	};

	const giftCardErrorMessages = useGiftCardErrorMessages();

	const addGiftCardToCart = async (
		values: GiftCardFormValues,
	): Promise<{ errorMessage?: string | undefined } | undefined> => {
		if (cart && cartHasGiftCardPayment(cart)) {
			return new Promise((resolve) =>
				resolve({ errorMessage: giftCardErrorMessages.invalid_method }),
			);
		}

		const { recipient, email, message, sender } = values;
		try {
			await addItemsToCart([
				{
					product,
					variant,
					additionalData: {
						recipientEmail: email,
						recipientName: recipient,
						senderName: sender,
						message: stripHtml(message || "").result,
					},
				},
			]);
		} catch {
			return new Promise((resolve, reject) =>
				reject(new Error("An error occured while adding an item to the cart")),
			);
		}
	};

	const paymentIcons = paymentMethods.map((item, index) => (
		<Icon key={index} icon={item} width={40} height={24} />
	));

	useKlaviyoViewedProduct(product, variant, firstLifestyleImage?.imgSrc ?? "");

	//modal handler
	const [modal, openModal] = useState<
		Omit<ProductPageModalProps, "open"> | undefined
	>();

	const [, setLocked] = useScrollBlock();

	useEffect(() => {
		setLocked(!!modal);
	}, [modal, setLocked]);

	useEffect(() => {
		const close = (e: { key: string }) => {
			if (!!modal && e.key === "Escape") {
				openModal(undefined);
			}
		};
		window.addEventListener("keydown", close);
		return () => window.removeEventListener("keydown", close);
	}, [modal, openModal]);

	const [pageLocation, setPageLocation] = useState<string>("");
	useEffect(() => {
		setPageLocation(window.location.href);
	}, []);

	useEffect(() => {
		sendBrowserEvent("ViewVariant", {
			product,
			variant,
		});
	}, [product, variant]);

	const fewInStock = stockQuantity <= 30 && stockQuantity > 0;

	const inStockLabel = fewInStock ? t("(Few in stock)") : t("(In stock)");

	const rawPrice = getVariantPrice(variant, siteConfig.currency, userCountry);

	const babyListButton =
		product.name && productImage && showBabyListButton ? (
			<BabyListButton
				images={[productImage.imgSrc]}
				price={formatPrice(siteConfig.currency, moneyToNumber(rawPrice))}
				title={`${product.name}: ${variantName}`}
				url={pageLocation}
			/>
		) : undefined;
	const myRegistryButton =
		product.name && productImage && showMyRegistryButton ? (
			<MyRegistryButton
				Title={`${product.name}: ${variantName}`}
				ImageUrl={productImage.imgSrc}
				URL={pageLocation}
				Price={moneyToNumber(rawPrice)}
				Color={color ?? ""}
				SKU={variant.sku ?? ""}
				CurrencyCode={siteConfig.currency}
			/>
		) : undefined;
	const waitListForm = (
		<SingleInputForm
			id="wait-list-form"
			errorMessage={waitListErrorMessage}
			successMessage={waitListSuccessMessage}
			placeholder={t("Email")}
			buttonLabel={t("Send")}
			loading={waitListLoading}
			onFormSubmit={onWaitListFormSubmit}
			onInputChange={onWaitListInputChange}
			input={waitListInput}
			type="email"
			removeButtonLabel={t("Remove")}
			required
		/>
	);

	const sortedVariants = variants.sort((a, b) => {
		// Use defaultSortOrder as the default value if sort_order is unset
		const defaultSortOrder = 99999;
		const sortOrderA = a.sort_order || defaultSortOrder;
		const sortOrderB = b.sort_order || defaultSortOrder;

		return sortOrderA - sortOrderB;
	});

	const [showQuickSearch, setShowQuickSearch] = useState(false);
	const hasBazaarVoice = useHasBazaarVoice();

	useEffect(() => {
		if (scrollPos > 0) {
			window.scrollTo(0, scrollPos);
			scrollPos = 0;
		}
	}, []);

	useEffect(() => {
		const headerHeight = isDesktop ? 160 : 104;

		const handleScroll = () => {
			if (window.scrollY < headerHeight) {
				setShowQuickSearch(true);
			} else {
				setShowQuickSearch(false);
			}
		};

		handleScroll();

		window.addEventListener("scroll", handleScroll);

		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, [isDesktop]);

	return (
		<>
			<BackgroundTheme themeColor={backgroundColor} />
			<ScrollSpy onChange={onChangeStickyNavLink}>
				<PageContainerComponent showQuickSearch={showQuickSearch}>
					<Breadcrumbs breadcrumbs={breadcrumbs} />
					{product.name && (
						<div itemScope itemType="https://schema.org/Product">
							<meta
								itemProp="name"
								content={`${product.name} - ${
									fabric ? ` ${fabric},` : ""
								} ${color}`}
							/>
							<meta itemProp="image" content={productImage?.imgSrc} />
							<ProductPageComponent>
								{productImage ? (
									<ProductGallery
										badge={badge}
										productImage={productImage}
										lookImage={firstLifestyleImage}
										restImages={sideBySideImages}
									/>
								) : null}

								<ProductPane>
									{isGiftCard ? (
										<GiftCard
											submit={(values) => addGiftCardToCart(values)}
											amounts={giftCards.map((card) => ({
												...card,
												active: card.id === variant.id,
											}))}
											productName={t("Babybjörn Giftcards")}
										/>
									) : (
										<ScrollSpyItem id="product">
											<PriceModule
												sku={variant.sku ?? ""}
												productBuyButtonId={productBuyButtonId}
												productName={product.name}
												ariaLabel={t("Share")}
												shareClick={() =>
													openModal({
														children: (
															<ShareListItem
																socials={[
																	{
																		icon: "facebook",
																		label: "Facebook",
																		onClick: () => facebook(),
																	},
																	{
																		icon: "pinterest",
																		label: "Pinterest",
																		onClick: () =>
																			pinterest(productImage?.imgSrc ?? ""),
																	},
																]}
															/>
														),
														heading: t("Send or share"),
													})
												}
												productOptions={
													<ProductOptions priceModule>
														{variants.length > 0 && (
															<VariantPicker
																colorLabel={t("Color")}
																fabricLabel={t("Fabric")}
																toyLabel={t("Toy")}
																chosenColor={getVariantColorLabelFromFragment(
																	variant.attributes,
																)}
																chosenFabric={getVariantFabricLabelFromFragment(
																	variant.attributes,
																)}
																chosenToy={toy ?? ""}
																materialLabel={t("Material")}
																openMaterialModal={
																	materials && materials.length > 0
																		? () =>
																				openModal({
																					heading: product.name,
																					description: t(
																						"Select the one that suits you and your child",
																					),
																					children: (
																						<Materials materials={materials} />
																					),
																				})
																		: undefined
																}
															>
																{sortedVariants.map((variant) => (
																	<Link
																		key={variant.id}
																		href={
																			variant.id === activeVariant?.id
																				? undefined
																				: variant.url
																		}
																		onClick={() => (scrollPos = window.scrollY)}
																		title={getVariantLabelFromVariant(
																			variant,
																			true,
																		)}
																	>
																		<VariantItem
																			id={variant.id}
																			image={{
																				src: variant.variantSymbolImage.src,
																				alt:
																					variant.variantSymbolImage.alt ?? "",
																			}}
																			fabric={
																				(variant.fabric &&
																					variant.fabric
																						.replaceAll(/\W+/g, "-")
																						.replaceAll(/^-+|-+$/g, "")
																						.toLowerCase()) ??
																				""
																			}
																			color={variant.color
																				.replaceAll(/\W+/g, "-")
																				.replaceAll(/^-+|-+$/g, "")
																				.toLowerCase()}
																			activeVariant={activeVariant?.id ?? ""}
																		/>
																	</Link>
																))}
															</VariantPicker>
														)}
													</ProductOptions>
												}
												rawPrice={rawPrice}
												inStock={inStock}
												shippingInfo={shippingInfo}
												deliveryInfo={deliveryInfo}
												giftwrapInfo={giftWrapInfo}
												addToCartLabel={buttonText}
												inStockLabel={inStockLabel}
												fewInStock={fewInStock}
												outOfStockLabel={t("(Not in stock)")}
												addToCart={() => addToCart()}
												paymentMethods={paymentIcons}
												buttons={priceModuleButtons}
												babyListButton={babyListButton}
												myRegistryButton={myRegistryButton}
												waitListLabel={t("Sign up for waiting list")}
												waitListForm={waitListForm}
												waitListToggleInput={waitListToggleInput}
												waitListShowInput={waitListShowInput}
												waitListDisabled={disableNotifyMe}
											/>
										</ScrollSpyItem>
									)}
									{tabs.length > 0 && (
										<div className={tabStyles.tabWrapper}>
											<TabGroup
												selectedTabIndex={selectedTabIndex}
												setSelectedTabIndex={onChangeTab}
												tabLabels={tabs.map((tab) => tab.label ?? "")}
												ariaLabelScroll={t("Show more")}
											/>
											{tabs.map((tab, index) => {
												if (selectedTabIndex === index && tab.items) {
													return (
														<ModuleRenderer
															key={index}
															modules={tab.items.map(
																(item) => item as Mutable<typeof item>,
															)}
														/>
													);
												}
												return null;
											})}
										</div>
									)}
									{accessoryProductCardProps &&
										accessoryProductCardProps.length > 0 && (
											<MoreSalesList
												heading={t("You may also like")}
												paddedItems={true}
											>
												{accessoryProductCardProps.map(
													(productCardData, index) => (
														<MoreSalesBanner
															image={productCardData.image}
															key={index}
															productName={productCardData.name}
															price={formatMoney(productCardData.rawPrice)}
															fabric={productCardData.fabric ?? ""}
															color={productCardData.color}
															padding={true}
															href={productCardData.url}
															outOfStock={!productCardData.isOnStock}
														>
															{productCardData.children}
														</MoreSalesBanner>
													),
												)}
											</MoreSalesList>
										)}
								</ProductPane>
							</ProductPageComponent>

							{accordionItems.length > 0 && (
								<ScrollSpyItem id="product-details">
									<ProductPageComponent>
										<GridItem>
											<AccordionModule items={accordionItems} />
										</GridItem>
									</ProductPageComponent>
								</ScrollSpyItem>
							)}

							<ProductPageComponent>
								{modules && <ModuleRenderer modules={modules} />}
							</ProductPageComponent>
						</div>
					)}

					{hasBazaarVoice && !isGiftCard && (
						<>
							<ScrollSpyItem id="reviews">
								{variant.sku && (
									<ProductPageComponent>
										<GridItem>
											<BazaarWidget type="reviews" productSku={variant.sku} />
										</GridItem>
									</ProductPageComponent>
								)}
							</ScrollSpyItem>
							<GridItem
								fullWidth
								style={{ position: "sticky", bottom: 0, zIndex: 10 }}
							>
								<ProductStickyNav
									price={formatMoney(rawPrice)}
									hidden={hideStickyNav}
									selectedTabId={activeStickyNavLink}
									setSelectedTabId={setActiveStickyNavLink}
									tabs={stickyNavAnchorTagTabs}
									addToCart={inStock ? addToCart : undefined}
								/>
							</GridItem>
						</>
					)}

					<ProductPageModal
						open={!!modal}
						heading={modal?.heading}
						description={modal?.description ?? ""}
						onClose={() => openModal(undefined)}
					>
						{modal?.children}
					</ProductPageModal>
				</PageContainerComponent>
			</ScrollSpy>
		</>
	);
};

const placeholderProduct: ProductCardProps = {
	sku: "098078US",
	name: "[PRODUCT NAME]",
	rawPrice: {
		centAmount: 9995,
		currencyCode: "USD",
		fractionDigits: 2,
		type: "centPrecision",
	},
	image: (
		<ImageContainer
			alt="Placeholder product image"
			ratio="portrait"
			objectFit="cover"
			imgSrc="https://placehold.jp/30/666666/ffffff/500x500.png?text=Product+image"
		/>
	),
	fabric: "[FABRIC]",
	color: "[COLOR]",
	isNew: true,
	newLabel: "[LABEL]",
	children: <></>,
	linkComponent: (children) => <Link href="">{children}</Link>,
};

export default ProductPage;
